import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Paper, Button, Chip,
  CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Snackbar, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, IconButton,
} from '@mui/material';
import {
  People, Business, AttachMoney, PersonAdd,
  BusinessCenter, CalendarMonth, Assessment, HourglassBottom,
  ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import apiClient from '../api/client';
import { useAuth } from '../contexts/AuthContext';

interface DashboardStats {
  activeEmployees: number;
  activeClients: number;
  totalBillableHours: number;
  totalRevenue: number;
  currentMonth: number;
}

interface PnLMonth {
  month: number;
  revenue: number;
  kovil_cost: number;
  gross_profit: number;
  gross_margin_pct: number | null;
  draw_sahdev: number;
  draw_davis: number;
  marketing_expenses: number;
  other_expenses: number;
  expenses: number;
  net_profit: number;
  net_margin_pct: number | null;
}

type ExpenseField = 'draw_sahdev' | 'draw_davis' | 'marketing_expenses' | 'other_expenses';

interface AnalyticsData {
  stats: { total_revenue: number };
  clientRevenue: Array<{
    client_name: string;
    client_code: string;
    employee_count: number;
    billable_hours: number;
    revenue: number;
  }>;
  topEmployees: Array<{
    employee_name: string;
    employee_uid: string;
    client_name: string;
    hourly_rate: number;
    billable_hours: number;
    revenue: number;
  }>;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatCurrency(value: number): string {
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
  return `$${value.toFixed(0)}`;
}

function ClientChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: 1.5,
      px: 2, py: 1.5, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, color: '#10B981' }}>
        Revenue: ${Number(payload[0]?.value || 0).toLocaleString()}
      </Typography>
    </Box>
  );
}

function EmployeeChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: 1.5,
      px: 2, py: 1.5, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, color: '#3B82F6' }}>
        Revenue: ${Number(payload[0]?.value || 0).toLocaleString()}
      </Typography>
    </Box>
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  // P&L state
  const [pnlYear, setPnlYear] = useState(new Date().getFullYear());
  const [pnlData, setPnlData] = useState<PnLMonth[]>([]);
  const [pnlLoading, setPnlLoading] = useState(true);
  const [editingExpense, setEditingExpense] = useState<{ month: number; field: ExpenseField; value: string } | null>(null);
  const expenseInputRef = useRef<HTMLInputElement>(null);

  // Generate timesheets dialog
  const [genOpen, setGenOpen] = useState(false);
  const [genMonth, setGenMonth] = useState(new Date().getMonth() + 1);
  const [genYear, setGenYear] = useState(new Date().getFullYear());
  const [genLoading, setGenLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      const [statsRes, analyticsRes] = await Promise.all([
        apiClient.get('/timesheets/stats'),
        apiClient.get('/reports/analytics', { params: { month, year } }),
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPnL = useCallback(async (year: number) => {
    setPnlLoading(true);
    try {
      const { data } = await apiClient.get('/reports/pnl', { params: { year } });
      setPnlData(data.months);
    } catch (err) {
      console.error('P&L fetch error:', err);
    } finally {
      setPnlLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => { fetchPnL(pnlYear); }, [fetchPnL, pnlYear]);

  const handleSaveExpense = async (month: number, field: ExpenseField, value: string) => {
    const amount = parseFloat(value) || 0;
    setEditingExpense(null);
    setPnlData(prev => prev.map(row => {
      if (row.month !== month) return row;
      const updated = { ...row, [field]: amount };
      const expenses = updated.draw_sahdev + updated.draw_davis + updated.marketing_expenses + updated.other_expenses;
      const netProfit = row.gross_profit - expenses;
      return { ...updated, expenses, net_profit: netProfit, net_margin_pct: row.revenue > 0 ? (netProfit / row.revenue) * 100 : null };
    }));
    try {
      await apiClient.put('/reports/pnl/expenses', { year: pnlYear, month, field, value: amount });
    } catch (err) {
      console.error('Save expense error:', err);
      fetchPnL(pnlYear);
    }
  };

  const handleGenerate = async () => {
    setGenLoading(true);
    try {
      const { data } = await apiClient.post('/timesheets/generate', { month: genMonth, year: genYear });
      setSnackbar({ message: data.message, severity: 'success' });
      setGenOpen(false);
      fetchData();
    } catch (err: any) {
      setSnackbar({ message: err.response?.data?.error || 'Failed to generate', severity: 'error' });
    } finally {
      setGenLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  const displayName = user?.profile && 'name' in user.profile ? user.profile.name : user?.username || 'Admin';
  const firstName = displayName.split(' ')[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const statCards = [
    { label: 'Active Employees', value: stats?.activeEmployees ?? 0, icon: <People />, color: '#3B82F6', bg: '#DBEAFE', onClick: () => navigate('/admin/employees') },
    { label: 'Active Clients', value: stats?.activeClients ?? 0, icon: <Business />, color: '#10B981', bg: '#D1FAE5', onClick: () => navigate('/admin/clients') },
    { label: `Billable Hours (${stats?.currentMonth ? MONTHS[stats.currentMonth - 1] : ''})`, value: stats?.totalBillableHours?.toFixed(1) ?? '0.0', icon: <HourglassBottom />, color: '#F59E0B', bg: '#FEF3C7', onClick: () => navigate('/admin/timesheets') },
    { label: `Revenue (${stats?.currentMonth ? MONTHS[stats.currentMonth - 1] : ''})`, value: `$${(stats?.totalRevenue ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, icon: <AttachMoney />, color: '#8B5CF6', bg: '#EDE9FE', onClick: () => navigate('/admin/employees') },
  ];

  const quickActions = [
    { label: 'Add Employee', icon: <PersonAdd sx={{ fontSize: 18 }} />, onClick: () => navigate('/admin/employees?action=add') },
    { label: 'Add Client', icon: <BusinessCenter sx={{ fontSize: 18 }} />, onClick: () => navigate('/admin/clients?action=add') },
    { label: 'Generate Timesheets', icon: <CalendarMonth sx={{ fontSize: 18 }} />, onClick: () => setGenOpen(true) },
    { label: 'View Reports', icon: <Assessment sx={{ fontSize: 18 }} />, onClick: () => navigate('/admin/reports') },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25, lineHeight: 1.3, letterSpacing: '-0.02em' }}>
          {greeting}, {firstName}
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#94A3B8', fontWeight: 400 }}>
          {dateStr}
        </Typography>
      </Box>

      {/* Stat Cards — 4-column grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 2,
        mb: 2.5,
      }}>
        {statCards.map((card) => (
          <Card
            key={card.label}
            sx={{
              cursor: 'pointer',
              borderLeft: `3px solid ${card.color}`,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderColor: card.color,
              },
            }}
            onClick={card.onClick}
          >
            <CardContent sx={{ p: '16px 18px !important', display: 'flex', alignItems: 'center', gap: 1.75 }}>
              <Box sx={{
                width: 46, height: 46, borderRadius: '12px', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                backgroundColor: card.bg, color: card.color, flexShrink: 0,
                '& .MuiSvgIcon-root': { fontSize: 22 },
              }}>
                {card.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 28, lineHeight: 1, mb: 0.4, color: '#0F172A', letterSpacing: '-0.02em' }}>
                  {card.value}
                </Typography>
                <Typography sx={{
                  fontSize: 11, fontWeight: 600, color: '#64748B',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }} noWrap>
                  {card.label}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Quick Actions — horizontal bar */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
        {quickActions.map((action) => (
          <Button
            key={action.label}
            size="small"
            variant="outlined"
            startIcon={action.icon}
            onClick={action.onClick}
            sx={{
              fontWeight: 500,
              fontSize: 12.5,
              py: 0.7,
              px: 1.75,
              borderRadius: '8px',
              color: '#475569',
              borderColor: '#E2E8F0',
              '& .MuiButton-startIcon': { color: '#64748B' },
              '&:hover': {
                backgroundColor: '#EFF6FF',
                color: '#3B82F6',
                borderColor: '#BFDBFE',
                '& .MuiButton-startIcon': { color: '#3B82F6' },
              },
            }}
          >
            {action.label}
          </Button>
        ))}
      </Box>

      {/* Charts Row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
        {/* Revenue by Client — Bar Chart */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>
              Revenue by Client
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
              {stats?.currentMonth ? MONTHS[stats.currentMonth - 1] : ''}
            </Typography>
          </Box>
          {(analytics?.clientRevenue?.length ?? 0) > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={analytics!.clientRevenue.slice(0, 6)} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis
                  dataKey="client_name" tick={{ fontSize: 11, fill: '#64748B' }}
                  axisLine={{ stroke: '#E2E8F0' }} tickLine={false}
                  interval={0} angle={(analytics?.clientRevenue?.length ?? 0) > 3 ? -25 : 0}
                  textAnchor={(analytics?.clientRevenue?.length ?? 0) > 3 ? 'end' : 'middle'}
                  height={(analytics?.clientRevenue?.length ?? 0) > 3 ? 55 : 30}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false}
                  tickFormatter={(v: number) => formatCurrency(v)} width={55}
                />
                <Tooltip content={<ClientChartTooltip />} cursor={{ fill: '#F8FAFC' }} />
                <Bar dataKey="revenue" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={52} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary" fontSize={13}>No revenue data for this month</Typography>
            </Box>
          )}
        </Paper>

        {/* Revenue by Employee — Bar Chart */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>
              Revenue by Employee
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
              Top {Math.min(analytics?.topEmployees?.length ?? 0, 6)}
            </Typography>
          </Box>
          {(analytics?.topEmployees?.length ?? 0) > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={analytics!.topEmployees.slice(0, 6)} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis
                  dataKey="employee_name" tick={{ fontSize: 11, fill: '#64748B' }}
                  axisLine={{ stroke: '#E2E8F0' }} tickLine={false}
                  interval={0} angle={(analytics?.topEmployees?.length ?? 0) > 3 ? -25 : 0}
                  textAnchor={(analytics?.topEmployees?.length ?? 0) > 3 ? 'end' : 'middle'}
                  height={(analytics?.topEmployees?.length ?? 0) > 3 ? 55 : 30}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false}
                  tickFormatter={(v: number) => formatCurrency(v)} width={55}
                />
                <Tooltip content={<EmployeeChartTooltip />} cursor={{ fill: '#F8FAFC' }} />
                <Bar dataKey="revenue" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={52} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary" fontSize={13}>No employee data for this month</Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Tables Row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5 }}>
        {/* Top Employees by Revenue */}
        <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #E2E8F0' }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>
              Top Employees by Revenue
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Hours</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Rate</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!analytics?.topEmployees?.length ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                      No data for this month
                    </TableCell>
                  </TableRow>
                ) : analytics.topEmployees.map((emp, idx) => (
                  <TableRow key={emp.employee_uid} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                          width: 28, height: 28, borderRadius: '50%',
                          backgroundColor: idx < 3 ? '#EFF6FF' : '#F8FAFC',
                          color: idx < 3 ? '#3B82F6' : '#94A3B8',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, flexShrink: 0,
                        }}>
                          {idx + 1}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#0F172A', fontSize: 13 }}>
                            {emp.employee_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: 11 }}>
                            {emp.client_name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontSize={13}>{emp.billable_hours.toFixed(1)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontSize={13} color="text.secondary">
                        {emp.hourly_rate > 0 ? `$${emp.hourly_rate}` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontSize={13} fontWeight={600} sx={{ color: '#0F172A' }}>
                        {emp.revenue > 0 ? `$${emp.revenue.toLocaleString()}` : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Client Breakdown */}
        <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #E2E8F0' }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>
              Client Breakdown
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Team</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Hours</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!analytics?.clientRevenue?.length ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                      No data for this month
                    </TableCell>
                  </TableRow>
                ) : analytics.clientRevenue.map((row) => {
                  const totalRevenue = analytics.stats.total_revenue || 1;
                  const pct = Math.round((row.revenue / totalRevenue) * 100);
                  return (
                    <TableRow key={row.client_code} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#0F172A', fontSize: 13 }}>
                            {row.client_name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Box sx={{
                              width: `${Math.max(pct, 4)}%`, maxWidth: '80%', height: 4,
                              borderRadius: 2, backgroundColor: '#10B981', opacity: 0.6,
                            }} />
                            <Typography sx={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>
                              {pct}%
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={row.employee_count} size="small" variant="outlined"
                          sx={{ fontSize: 12, fontWeight: 600, minWidth: 32 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontSize={13}>{row.billable_hours.toFixed(1)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontSize={13} fontWeight={600} sx={{ color: '#0F172A' }}>
                          {row.revenue > 0 ? `$${row.revenue.toLocaleString()}` : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* P&L Card */}
      <Paper variant="outlined" sx={{ mt: 2.5, overflow: 'hidden' }}>
        {/* Card header */}
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>
            P&L Overview
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={() => setPnlYear(y => y - 1)}><ChevronLeft sx={{ fontSize: 18 }} /></IconButton>
            <Typography sx={{ fontWeight: 700, fontSize: 14, minWidth: 42, textAlign: 'center', color: '#1E40AF' }}>{pnlYear}</Typography>
            <IconButton size="small" onClick={() => setPnlYear(y => y + 1)}><ChevronRight sx={{ fontSize: 18 }} /></IconButton>
          </Box>
        </Box>

        {pnlLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1300 }}>
              <TableHead>
                {/* Group header row */}
                <TableRow>
                  {[
                    { label: 'Month',                    rowSpan: 2, width: '6%',  align: 'left'   as const },
                    { label: 'Revenue',                  rowSpan: 2, width: '8%',  align: 'right'  as const },
                    { label: 'Kovil AI Cost (Resource)', rowSpan: 2, width: '9%',  align: 'right'  as const },
                    { label: 'Gross Profit Margin',      colSpan: 2, width: '14%', align: 'center' as const },
                    { label: 'Expenses',                 colSpan: 5, width: '41%', align: 'center' as const },
                    { label: 'Net Profit Margin',        colSpan: 2, width: '14%', align: 'center' as const },
                  ].map((col, i) => (
                    <TableCell
                      key={i}
                      rowSpan={'rowSpan' in col ? col.rowSpan : undefined}
                      colSpan={'colSpan' in col ? col.colSpan : undefined}
                      align={col.align}
                      sx={{
                        width: col.width,
                        bgcolor: '#1E3A5F', color: '#fff',
                        fontWeight: 700, fontSize: '0.72rem',
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                        borderRight: '1px solid #2D4F7A',
                        borderBottom: '1px solid #2D4F7A',
                        py: 1.25,
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
                {/* Sub-header row */}
                <TableRow>
                  {[
                    { label: 'Margin ($)',                  align: 'right'  as const, blue: false },
                    { label: 'Margin (%)',                  align: 'right'  as const, blue: false },
                    { label: "Founder's Draw – Sahdev",     align: 'right'  as const, blue: false },
                    { label: "Founder's Draw – Davis",      align: 'right'  as const, blue: false },
                    { label: 'Marketing / Outreach',        align: 'right'  as const, blue: false },
                    { label: 'Other Expenses',              align: 'right'  as const, blue: false },
                    { label: 'Total Expenses',              align: 'right'  as const, blue: true  },
                    { label: 'Margin ($)',                  align: 'right'  as const, blue: false },
                    { label: 'Margin (%)',                  align: 'right'  as const, blue: false },
                  ].map((sub, i) => (
                    <TableCell key={i} align={sub.align} sx={{
                      bgcolor: sub.blue ? '#1A4A7A' : '#2D4F7A',
                      color: sub.blue ? '#BAE6FD' : '#CBD5E1',
                      fontWeight: 600, fontSize: '0.67rem',
                      textTransform: 'uppercase', letterSpacing: '0.03em',
                      borderRight: '1px solid #3A6090',
                      borderBottom: '2px solid #3A6090',
                      py: 1,
                    }}>
                      {sub.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {pnlData.map((row, idx) => {
                  const hasData = row.revenue > 0 || row.kovil_cost > 0;
                  const rowBg = idx % 2 === 0 ? '#FFFFFF' : '#F8FAFF';
                  const fmt = (v: number) => v === 0 ? '—' : `$${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                  const fmtPct = (v: number | null) => v === null ? '—' : `${v.toFixed(1)}%`;
                  const borderR = { borderRight: '1px solid #E2E8F0' };

                  const expCell = (field: ExpenseField) => {
                    const isEditingThis = editingExpense?.month === row.month && editingExpense?.field === field;
                    const val = row[field] as number;
                    return (
                      <TableCell
                        key={field}
                        align="right"
                        sx={{ ...borderR, py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: '#FEF9C3' } }}
                        onClick={() => {
                          setEditingExpense({ month: row.month, field, value: val > 0 ? String(val) : '' });
                          setTimeout(() => expenseInputRef.current?.focus(), 50);
                        }}
                      >
                        {isEditingThis ? (
                          <TextField
                            inputRef={expenseInputRef}
                            variant="standard"
                            value={editingExpense!.value}
                            onChange={e => setEditingExpense({ month: row.month, field, value: e.target.value })}
                            onBlur={() => handleSaveExpense(row.month, field, editingExpense!.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleSaveExpense(row.month, field, editingExpense!.value);
                              if (e.key === 'Escape') setEditingExpense(null);
                            }}
                            inputProps={{ style: { textAlign: 'right', fontSize: 12, width: 72 } }}
                            InputProps={{ startAdornment: <Typography sx={{ fontSize: 12, color: '#64748B', mr: 0.25 }}>$</Typography> }}
                            sx={{ '& .MuiInput-underline:before': { borderColor: '#3B82F6' } }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 12, color: val > 0 ? '#0F172A' : '#94A3B8', fontStyle: val > 0 ? 'normal' : 'italic', fontWeight: val > 0 ? 500 : 400 }}>
                            {val > 0 ? fmt(val) : 'Enter'}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  };

                  return (
                    <TableRow key={row.month} sx={{ bgcolor: rowBg, '&:hover': { bgcolor: '#EFF6FF' } }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: 13, color: '#1E3A5F', ...borderR, py: 1 }}>
                        {MONTH_ABBR[row.month - 1]}-{String(pnlYear).slice(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, color: hasData ? '#0F172A' : 'text.disabled', ...borderR, py: 1 }}>
                        {fmt(row.revenue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, color: hasData ? '#0F172A' : 'text.disabled', ...borderR, py: 1 }}>
                        {fmt(row.kovil_cost)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontWeight: hasData ? 600 : 400, color: hasData ? (row.gross_profit >= 0 ? '#15803D' : '#DC2626') : 'text.disabled', ...borderR, py: 1 }}>
                        {fmt(row.gross_profit)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, color: row.gross_margin_pct !== null ? '#64748B' : 'text.disabled', ...borderR, py: 1 }}>
                        {fmtPct(row.gross_margin_pct)}
                      </TableCell>
                      {expCell('draw_sahdev')}
                      {expCell('draw_davis')}
                      {expCell('marketing_expenses')}
                      {expCell('other_expenses')}
                      {/* Total Expenses — calculated, light blue */}
                      <TableCell align="right" sx={{ fontSize: 13, fontWeight: row.expenses > 0 ? 600 : 400, color: row.expenses > 0 ? '#0F172A' : 'text.disabled', bgcolor: '#EFF9FF', ...borderR, py: 1 }}>
                        {fmt(row.expenses)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontWeight: hasData ? 600 : 400, color: hasData ? (row.net_profit >= 0 ? '#15803D' : '#DC2626') : 'text.disabled', ...borderR, py: 1 }}>
                        {fmt(row.net_profit)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, color: row.net_margin_pct !== null ? '#64748B' : 'text.disabled', py: 1 }}>
                        {fmtPct(row.net_margin_pct)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>

              <TableBody>
                {(() => {
                  const dataMonths = pnlData.filter(r => r.revenue > 0 || r.kovil_cost > 0 || r.expenses > 0);
                  const totRev    = pnlData.reduce((s, r) => s + r.revenue, 0);
                  const totCost   = pnlData.reduce((s, r) => s + r.kovil_cost, 0);
                  const totGross  = pnlData.reduce((s, r) => s + r.gross_profit, 0);
                  const totSahdev = pnlData.reduce((s, r) => s + r.draw_sahdev, 0);
                  const totDavis  = pnlData.reduce((s, r) => s + r.draw_davis, 0);
                  const totMktg   = pnlData.reduce((s, r) => s + r.marketing_expenses, 0);
                  const totOther  = pnlData.reduce((s, r) => s + r.other_expenses, 0);
                  const totExp    = pnlData.reduce((s, r) => s + r.expenses, 0);
                  const totNet    = pnlData.reduce((s, r) => s + r.net_profit, 0);
                  const gPctMonths = dataMonths.filter(r => r.gross_margin_pct !== null);
                  const avgGrossPct = gPctMonths.length > 0 ? gPctMonths.reduce((s, r) => s + r.gross_margin_pct!, 0) / gPctMonths.length : null;
                  const nPctMonths = dataMonths.filter(r => r.net_margin_pct !== null);
                  const avgNetPct = nPctMonths.length > 0 ? nPctMonths.reduce((s, r) => s + r.net_margin_pct!, 0) / nPctMonths.length : null;
                  const fmt = (v: number) => v === 0 ? '—' : `$${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                  const cellSx = { fontWeight: 700, fontSize: 13, py: 1.25, bgcolor: '#1E3A5F', color: '#fff', borderRight: '1px solid #2D4F7A' };
                  return (
                    <TableRow>
                      <TableCell sx={{ ...cellSx, color: '#93C5FD' }}>Total / Avg</TableCell>
                      <TableCell align="right" sx={cellSx}>{fmt(totRev)}</TableCell>
                      <TableCell align="right" sx={cellSx}>{fmt(totCost)}</TableCell>
                      <TableCell align="right" sx={{ ...cellSx, color: totGross >= 0 ? '#6EE7B7' : '#FCA5A5' }}>{fmt(totGross)}</TableCell>
                      <TableCell align="right" sx={{ ...cellSx, color: avgGrossPct !== null ? '#93C5FD' : '#64748B' }}>
                        {avgGrossPct !== null ? `${avgGrossPct.toFixed(1)}%` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={cellSx}>{fmt(totSahdev)}</TableCell>
                      <TableCell align="right" sx={cellSx}>{fmt(totDavis)}</TableCell>
                      <TableCell align="right" sx={cellSx}>{fmt(totMktg)}</TableCell>
                      <TableCell align="right" sx={cellSx}>{fmt(totOther)}</TableCell>
                      <TableCell align="right" sx={{ ...cellSx, bgcolor: '#1A4A7A', color: '#BAE6FD' }}>{fmt(totExp)}</TableCell>
                      <TableCell align="right" sx={{ ...cellSx, color: totNet >= 0 ? '#6EE7B7' : '#FCA5A5' }}>{fmt(totNet)}</TableCell>
                      <TableCell align="right" sx={{ ...cellSx, borderRight: 'none', color: avgNetPct !== null ? '#93C5FD' : '#64748B' }}>
                        {avgNetPct !== null ? `${avgNetPct.toFixed(1)}%` : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Generate Timesheets Dialog */}
      <Dialog open={genOpen} onClose={() => setGenOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 16 }}>Generate Monthly Timesheets</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#64748B' }}>
            Create empty timesheets for all active employees for the selected month.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select label="Month" size="small" fullWidth
              value={genMonth} onChange={(e) => setGenMonth(Number(e.target.value))}
            >
              {['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'].map((name, i) => (
                <MenuItem key={i + 1} value={i + 1}>{name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Year" type="number" size="small" fullWidth
              value={genYear} onChange={(e) => setGenYear(Number(e.target.value))}
              inputProps={{ min: 2024, max: 2030 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGenOpen(false)} sx={{ color: '#64748B' }}>Cancel</Button>
          <Button onClick={handleGenerate} variant="contained" disabled={genLoading}>
            {genLoading ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
